import type { EventPayload } from '@side/analytics';

enum AnalyticsEventName {
  LogoutRedirect = 'logout_redirect',
  SSOPlatformLogin = 'sso_platform_login',
  SSOOktaLogin = 'sso_okta_login',
  GoogleLogin = 'google_login',
  SSOLoginClick = 'sso_login_click',
  GoogleLoginClick = 'google_login_click',
  TeamNotFound = 'team_not_found',
  TeamMemberNotFound = 'team_member_not_found',
  ExternalServiceAddClick = 'external_service_add_click',
  ExternalServiceSaveClick = 'external_service_save_click',
  ExternalServiceRemoveClick = 'external_service_remove_click',
  ExternalServiceAddUserClick = 'external_service_add_user_click',
  TeamDetailTabMount = 'team_detail_tab_mount',
  TeamDetailTabUnmount = 'team_detail_tab_unmount',
  AuthorizeNotificationsDismissed = 'dismiss_authorize_notifications',
  ClickAuthorizeNotifications = 'authorize_notifications',
  // NOTE: Event names for notifications are all the same since they are used as
  // table name in data warehouse - for more information talk to data-eng
  ManageNotifications = 'MANAGE_NOTIFICATIONS',
  AppRefreshModal = 'APP_REFRESH_MODAL',
  AddTeamMember = 'ADD_TEAM_MEMBER',
  PaymentMethod = 'PAYMENT_METHOD',
  // General stepper event
  PageStepper = 'PAGE_STEPPER',
  ContactSync = 'CONTACT_SYNC',
  AccountManagement = 'ACCOUNT_MANAGEMENT',
  Sidebar = 'SIDEBAR',
}

export enum AnalyticsEvent {
  OktaLoginSuccess,
  OktaLoginFailure,
  GoogleLoginFailure,
  OktaLoginClick,
  OktaAutomaticLogin,
  GoogleLoginClick,
  OktaLogoutSuccess,
  OktaLogoutFailure,
  OktaFullLogout,
  TeamNotFound,
  TeamMemberNotFound,
  ExternalServiceAddClick,
  ExternalServiceSaveClick,
  ExternalServiceRemoveClick,
  ExternalServiceAddUserClick,
  TeamDetailTabMount,
  TeamDetailTabUnmount,
  ManageNotifications,
  AppRefreshModalMount,
  AppRefreshModalSnoozeClick,
  AppRefreshModalRefreshClick,
  AuthorizeNotificationsDismissed,
  ClickAuthorizeNotifications,
  AddTeamMemberPageLoad,
  AddTeamMemberCardButtonClick,
  AddTeamMemberCardOnboardingChecklistClick,
  AddTeamMemberCardLicensedClick,
  AddTeamMemberCardUnlicensedClick,
  AddTeamMemberCardRequestAccessClick,
  AddTeamMemberCancelButtonClick,
  AddTeamMemberNextButtonClick,
  AddTeamMemberCancelDialogMount,
  AddTeamMemberCancelDialogExit,
  AddTeamMemberCancelDialogConfirmationClick,
  AddTeamMemberFinalSubmitClick,
  AddTeamMemberFinalEditClick,
  AddTeamMemberSubmitFailureMount,
  AddTeamMemberRoleSelection,
  AddTeamMemberRoleSelectionHover,
  AddTeamMemberRoleTooltipDismiss,
  PaymentMethod,
  PaymentMethodAlertMount,
  PaymentMethodAlertCtaButtonClick,
  PageStepper,
  ConnectAccountClick,
  DisconnectAccountClick,
  AccountManagement,
  AccountManagementTeamMemberDetailDropdownClick,
  AccountManagementTeamMemberUpdateRoleButtonClick,
  AccountManagementTeamSelectorDropdownClick,
  AccountManagementTeamMemberListSortClick,
  AccountManagementTeamMemberFilterToggle,
  Sidebar,
}

type EventNames = {
  [key in AnalyticsEvent]: AnalyticsEventName;
};

type EventTypePayloads = {
  [key in AnalyticsEvent]: EventPayload & Record<string, unknown>;
};

export const AnalyticsEventNames: EventNames = {
  [AnalyticsEvent.OktaFullLogout]: AnalyticsEventName.LogoutRedirect,
  [AnalyticsEvent.OktaLogoutSuccess]: AnalyticsEventName.LogoutRedirect,
  [AnalyticsEvent.OktaLogoutFailure]: AnalyticsEventName.LogoutRedirect,
  [AnalyticsEvent.OktaLoginSuccess]: AnalyticsEventName.SSOPlatformLogin,
  [AnalyticsEvent.OktaLoginFailure]: AnalyticsEventName.SSOPlatformLogin,
  [AnalyticsEvent.GoogleLoginFailure]: AnalyticsEventName.GoogleLogin,
  [AnalyticsEvent.OktaLoginClick]: AnalyticsEventName.SSOLoginClick,
  [AnalyticsEvent.GoogleLoginClick]: AnalyticsEventName.GoogleLoginClick,
  [AnalyticsEvent.OktaAutomaticLogin]: AnalyticsEventName.SSOOktaLogin,
  [AnalyticsEvent.TeamNotFound]: AnalyticsEventName.TeamNotFound,
  [AnalyticsEvent.TeamMemberNotFound]: AnalyticsEventName.TeamMemberNotFound,
  [AnalyticsEvent.ExternalServiceAddClick]:
    AnalyticsEventName.ExternalServiceAddClick,
  [AnalyticsEvent.ExternalServiceSaveClick]:
    AnalyticsEventName.ExternalServiceSaveClick,
  [AnalyticsEvent.ExternalServiceRemoveClick]:
    AnalyticsEventName.ExternalServiceRemoveClick,
  [AnalyticsEvent.ExternalServiceAddUserClick]:
    AnalyticsEventName.ExternalServiceRemoveClick,
  [AnalyticsEvent.TeamDetailTabMount]: AnalyticsEventName.TeamDetailTabMount,
  [AnalyticsEvent.TeamDetailTabUnmount]:
    AnalyticsEventName.TeamDetailTabUnmount,
  [AnalyticsEvent.ManageNotifications]: AnalyticsEventName.ManageNotifications,
  [AnalyticsEvent.AppRefreshModalMount]: AnalyticsEventName.AppRefreshModal,
  [AnalyticsEvent.AppRefreshModalSnoozeClick]:
    AnalyticsEventName.AppRefreshModal,
  [AnalyticsEvent.AppRefreshModalRefreshClick]:
    AnalyticsEventName.AppRefreshModal,
  [AnalyticsEvent.AuthorizeNotificationsDismissed]:
    AnalyticsEventName.AuthorizeNotificationsDismissed,
  [AnalyticsEvent.ClickAuthorizeNotifications]:
    AnalyticsEventName.ClickAuthorizeNotifications,
  [AnalyticsEvent.AddTeamMemberPageLoad]: AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCardButtonClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCardOnboardingChecklistClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCardLicensedClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCardUnlicensedClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCardRequestAccessClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCancelButtonClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberNextButtonClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCancelDialogMount]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCancelDialogExit]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberCancelDialogConfirmationClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberFinalSubmitClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberFinalEditClick]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberSubmitFailureMount]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberRoleSelection]: AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberRoleSelectionHover]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.AddTeamMemberRoleTooltipDismiss]:
    AnalyticsEventName.AddTeamMember,
  [AnalyticsEvent.PaymentMethod]: AnalyticsEventName.PaymentMethod,
  [AnalyticsEvent.PaymentMethodAlertMount]: AnalyticsEventName.PaymentMethod,
  [AnalyticsEvent.PaymentMethodAlertCtaButtonClick]:
    AnalyticsEventName.PaymentMethod,
  [AnalyticsEvent.PageStepper]: AnalyticsEventName.PageStepper,
  [AnalyticsEvent.ConnectAccountClick]: AnalyticsEventName.ContactSync,
  [AnalyticsEvent.DisconnectAccountClick]: AnalyticsEventName.ContactSync,
  [AnalyticsEvent.AccountManagement]: AnalyticsEventName.AccountManagement,
  [AnalyticsEvent.AccountManagementTeamMemberDetailDropdownClick]:
    AnalyticsEventName.AccountManagement,
  [AnalyticsEvent.AccountManagementTeamMemberUpdateRoleButtonClick]:
    AnalyticsEventName.AccountManagement,
  [AnalyticsEvent.AccountManagementTeamSelectorDropdownClick]:
    AnalyticsEventName.AccountManagement,
  [AnalyticsEvent.AccountManagementTeamMemberListSortClick]:
    AnalyticsEventName.AccountManagement,
  [AnalyticsEvent.AccountManagementTeamMemberFilterToggle]:
    AnalyticsEventName.AccountManagement,
  [AnalyticsEvent.Sidebar]: AnalyticsEventName.Sidebar,
};

export const AnalyticsEventPayloads: EventTypePayloads = {
  [AnalyticsEvent.OktaLoginSuccess]: {
    eventContainer: 'sso_platform_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
  },
  [AnalyticsEvent.OktaLoginFailure]: {
    eventContainer: 'sso_platform_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.OktaLogoutSuccess]: {
    eventContainer: 'logout_identity',
    eventTarget: 'logout_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
  },
  [AnalyticsEvent.OktaFullLogout]: {
    eventContainer: 'logout_identity',
    eventTarget: 'logout_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
    multi_user: true,
  },
  [AnalyticsEvent.OktaLogoutFailure]: {
    eventContainer: 'logout_identity',
    eventTarget: 'logout_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
  },
  [AnalyticsEvent.GoogleLoginFailure]: {
    eventContainer: 'google_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.OktaLoginClick]: {
    eventContainer: 'sso_login_modal',
    eventTarget: 'sign_in_button',
    eventType: 'click',
    task: 'login',
  },
  [AnalyticsEvent.GoogleLoginClick]: {
    eventContainer: 'google_login_modal',
    eventTarget: 'sign_in_button',
    eventType: 'click',
    task: 'login',
  },
  [AnalyticsEvent.OktaAutomaticLogin]: {
    eventContainer: 'sso_okta_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
  },
  [AnalyticsEvent.TeamNotFound]: {
    eventContainer: 'team_detail_identity',
    eventTarget: 'team_not_found',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.TeamMemberNotFound]: {
    eventContainer: 'team_member_detail_identity',
    eventTarget: 'team_member_not_found',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.ExternalServiceAddClick]: {
    eventContainer: 'add_external_service_modal',
    eventTarget: 'add_service_button',
    eventType: 'click',
    redirect: false,
    task: 'add',
  },
  [AnalyticsEvent.ExternalServiceSaveClick]: {
    eventContainer: 'external_service_detail',
    eventTarget: 'save_service_button',
    eventType: 'click',
    redirect: false,
    task: 'update',
  },
  [AnalyticsEvent.ExternalServiceRemoveClick]: {
    eventContainer: 'remove_external_service_modal',
    eventTarget: 'remove_button',
    eventType: 'click',
    task: 'remove',
    redirect: false,
  },
  [AnalyticsEvent.ExternalServiceAddUserClick]: {
    eventContainer: 'add_external_service_user_modal',
    eventTarget: 'add_user_button',
    eventType: 'click',
    task: 'add',
    redirect: false,
  },
  [AnalyticsEvent.TeamDetailTabMount]: {
    eventContainer: 'team_detail_identity',
    eventTarget: 'team_view',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.TeamDetailTabUnmount]: {
    eventContainer: 'team_detail_identity',
    eventTarget: 'team_view',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.ManageNotifications]: {
    eventContainer: 'notifications_preference',
    eventTarget: 'notifications_preference',
    eventType: 'click',
    task: 'view',
  },
  [AnalyticsEvent.AppRefreshModalMount]: {
    eventContainer: 'app_version_refresh_modal',
    eventTarget: 'app_version_refresh_modal',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.AppRefreshModalSnoozeClick]: {
    eventContainer: 'app_version_refresh_modal',
    eventTarget: 'snooze_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AppRefreshModalRefreshClick]: {
    eventContainer: 'app_version_refresh_modal',
    eventTarget: 'refresh_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AuthorizeNotificationsDismissed]: {
    eventContainer: 'notifications_allow_modal',
    eventTarget: 'notifications_allow_modal',
    eventType: 'click',
    task: 'exit',
  },
  [AnalyticsEvent.ClickAuthorizeNotifications]: {
    eventContainer: 'notifications_allow_modal',
    eventTarget: 'allow_notifications_button',
    eventType: 'click',
    task: 'allow_notifications',
  },
  [AnalyticsEvent.AddTeamMemberPageLoad]: {
    eventContainer: 'select_roles_view', // Changed based on view
    eventTarget: 'select_roles_view', // Changed based on view
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.AddTeamMemberCardButtonClick]: {
    eventContainer: 'add_team_member_card',
    eventTarget: 'add_team_member_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AddTeamMemberCardOnboardingChecklistClick]: {
    eventContainer: 'add_team_member_card',
    eventTarget: 'onboarding_checklist_link',
    eventType: 'click',
    task: 'view',
  },
  [AnalyticsEvent.AddTeamMemberCardRequestAccessClick]: {
    eventContainer: 'add_team_member_card',
    eventTarget: 'request_access_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AddTeamMemberCardLicensedClick]: {
    eventContainer: 'add_team_member_card',
    eventTarget: 'licensed_form_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AddTeamMemberCardUnlicensedClick]: {
    eventContainer: 'add_team_member_card',
    eventTarget: 'unlicensed_form_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AddTeamMemberCancelButtonClick]: {
    eventContainer: 'select_roles_view', // Changes based on page
    eventTarget: 'cancel_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AddTeamMemberNextButtonClick]: {
    eventContainer: 'select_roles_view', // Changes based on page
    eventTarget: 'next_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AddTeamMemberCancelDialogMount]: {
    eventContainer: 'exit_warning_modal',
    eventTarget: 'exit_warning_modal',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.AddTeamMemberCancelDialogExit]: {
    eventContainer: 'exit_warning_modal',
    eventTarget: 'exit_warning_modal', // Changes based on method of exit
    eventType: 'click',
    task: 'exit',
  },
  [AnalyticsEvent.AddTeamMemberCancelDialogConfirmationClick]: {
    eventContainer: 'exit_warning_modal',
    eventTarget: 'confirm_cancellation_button',
    eventType: 'click',
    task: 'exit',
  },
  [AnalyticsEvent.AddTeamMemberFinalSubmitClick]: {
    eventContainer: 'final_details_view',
    eventTarget: 'submit_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AddTeamMemberFinalEditClick]: {
    eventContainer: 'final_details_view',
    eventTarget: 'edit_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AddTeamMemberSubmitFailureMount]: {
    eventContainer: 'final_details_view',
    eventTarget: 'failed_to_submit_toast',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.AddTeamMemberRoleSelection]: {
    eventContainer: 'select_roles_view',
    eventTarget: 'role_button',
    eventType: 'click',
    task: 'select', // Changes based on if role is selected or unselected
  },
  [AnalyticsEvent.AddTeamMemberRoleSelectionHover]: {
    eventContainer: 'select_roles_view',
    eventTarget: 'role_button',
    eventType: 'hover',
    task: 'view',
  },
  [AnalyticsEvent.AddTeamMemberRoleTooltipDismiss]: {
    eventContainer: 'select_roles_view',
    eventTarget: 'dismiss_tooltip_button',
    eventType: 'click',
    task: 'view',
  },
  [AnalyticsEvent.PageStepper]: {
    eventContainer: 'page_stepper_view',
    eventTarget: 'team_member_details_view', // Changes based on view
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.PaymentMethod]: {
    eventContainer: 'platform_fee_card',
    eventTarget: 'invoices_and_billing_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.PaymentMethodAlertMount]: {
    eventContainer: 'payment_method_alert_banner',
    eventTarget: 'payment_method_alert_banner',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.PaymentMethodAlertCtaButtonClick]: {
    eventContainer: 'payment_method_alert_banner',
    eventTarget: 'add_method_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.ConnectAccountClick]: {
    eventContainer: 'account_detail_view',
    eventTarget: 'connect_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.DisconnectAccountClick]: {
    eventContainer: 'account_detail_view',
    eventTarget: 'disconnect_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AccountManagement]: {
    eventContainer: 'account_profile',
    eventTarget: 'account_profile',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.AccountManagementTeamMemberDetailDropdownClick]: {
    eventContainer: 'team_management',
    eventTarget: 'team_member_detail_dropdown',
    eventType: 'click',
    task: 'expand', // Will be overridden with 'expand' or 'collapse' dynamically
  },
  [AnalyticsEvent.AccountManagementTeamMemberUpdateRoleButtonClick]: {
    eventContainer: 'team_member_detail_dropdown',
    eventTarget: 'update_role_button',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AccountManagementTeamSelectorDropdownClick]: {
    eventContainer: 'team_management',
    eventTarget: 'select_team_dropdown',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AccountManagementTeamMemberListSortClick]: {
    eventContainer: 'team_management',
    eventTarget: 'team_member_list_view',
    eventType: 'click',
    task: 'sort',
  },
  [AnalyticsEvent.Sidebar]: {
    eventContainer: 'sidebar',
    eventTarget: 'account_profile',
    eventType: 'click',
    task: 'navigate',
  },
  [AnalyticsEvent.AccountManagementTeamMemberFilterToggle]: {
    eventContainer: 'team_management',
    eventTarget: 'filter_dropdown',
    eventType: 'click',
    task: 'select',
  },
};
